import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { xeroService, XeroDirectIntegration, XeroDirectTenant, XeroSyncHistory } from '../services/xeroService';
import { integrationService } from '../services/integrationService';
import ToastNotification from '../components/ToastNotification';
import ConfirmationModal from '../components/ConfirmationModal';
import IntegrationUrlModal from '../components/IntegrationUrlModal';
import { formatTimestampWithTimezone } from '../utils/timeHelpers';

interface IntegrationDetailsProps { }

const IntegrationDetails: React.FC<IntegrationDetailsProps> = () => {
  const { integrationId } = useParams<{ integrationId: string }>();
  const navigate = useNavigate();
  const { accessToken, setUserAndToken } = useUser();
  
  const [integration, setIntegration] = useState<XeroDirectIntegration | null>(null);
  const [tenants, setTenants] = useState<XeroDirectTenant[]>([]);
  const [syncHistory, setSyncHistory] = useState<XeroSyncHistory | null>(null);
  const [organizationIntegration, setOrganizationIntegration] = useState<any>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [showScopeModal, setShowScopeModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);
  const [integrationUrl, setIntegrationUrl] = useState('');

  useEffect(() => {
    if (accessToken && integrationId) {
      loadIntegrationData();
    }
  }, [accessToken, integrationId]);

  const loadIntegrationData = async () => {
    try {
      setLoading(true);
      
      if (!integrationId) {
        throw new Error('Integration ID is required');
      }

      // Get integration details using the new generic endpoint
      const response = await fetch(`${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000'}/api/v1/integrations/${integrationId}/details`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch integration details');
      }

      const integrationDetails = await response.json();
      
      if (integrationDetails.integration_type !== 'xero_direct') {
        throw new Error('This integration is not a Xero integration');
      }

      // Set the integration data (handle revoked and pending integrations where xero_integration might be null)
      if (integrationDetails.xero_integration) {
        setIntegration({
          id: integrationDetails.xero_integration.id,
          organization_id: integrationDetails.organization_integration.organization_id,
          access_token_enc: '', // Not exposed for security
          refresh_token_enc: '', // Not exposed for security
          id_token_enc: '', // Not exposed for security
          scope: integrationDetails.xero_integration.scope,
          token_type: integrationDetails.xero_integration.token_type,
          expires_at: integrationDetails.xero_integration.expires_at,
          created_at: integrationDetails.xero_integration.created_at,
          updated_at: integrationDetails.xero_integration.updated_at,
        });
      } else {
        // For revoked or pending integrations, create a minimal integration object
        const status = integrationDetails.organization_integration.authorization_status;
        const expiresAt = status === 'revoked' 
          ? integrationDetails.organization_integration.revoked_at 
          : integrationDetails.organization_integration.expired_at;
        
        setIntegration({
          id: integrationDetails.organization_integration.id,
          organization_id: integrationDetails.organization_integration.organization_id,
          access_token_enc: '',
          refresh_token_enc: '',
          id_token_enc: '',
          scope: status === 'pending' ? 'Pending Authorization' : 'N/A',
          token_type: status === 'pending' ? 'Pending' : 'N/A',
          expires_at: expiresAt || new Date().toISOString(),
          created_at: integrationDetails.organization_integration.created_at,
          updated_at: integrationDetails.organization_integration.updated_at,
        });
      }

      // Store organization integration data for status and revoke info
      setOrganizationIntegration(integrationDetails.organization_integration);

      setTenants(integrationDetails.tenants);

      // Load sync history for the first tenant (or all tenants)
      if (integrationDetails.tenants.length > 0) {
        const firstTenant = integrationDetails.tenants[0];
        const syncHistoryData = await xeroService.getSyncStatus(
          integrationDetails.organization_integration.organization_id,
          accessToken!,
          setUserAndToken,
          firstTenant.xero_tenant_id,
          50
        );
        
        setSyncHistory(syncHistoryData);
      } else {
        // If no tenants (e.g., after revocation), clear the sync history
        setSyncHistory(null);
      }
      
      // Check if automatic initial sync was triggered
      if (integrationDetails.organization_integration.integration_data) {
        const integrationData = integrationDetails.organization_integration.integration_data;
        if (Array.isArray(integrationData)) {
          const autoSyncEvent = integrationData.find(event =>
            event.auto_sync_triggered ||
            (event.auto_triggered && event.triggered_by === 'authorization_callback')
          );
          
          if (autoSyncEvent) {
            setToastMsg('Initial sync has been automatically triggered after authorization. Data is being synchronized in the background.');
          }
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading integration data:', error);
      setError('Failed to load integration data');
      setLoading(false);
    }
  };

  const handleRevokeIntegration = () => {
    setShowRevokeModal(true);
  };

  const confirmRevokeIntegration = async () => {
    if (!integration || !integrationId) return;
    
    try {
      setShowRevokeModal(false);
      // Revoke the organization integration using the OrganizationIntegration.id
      await integrationService.revokeOrganizationIntegration(
        integration.organization_id,
        integrationId,
        accessToken!,
        setUserAndToken
      );
      setToastMsg('Integration revoked successfully');
      
      // Immediately clear sync history to show the UI update
      setSyncHistory(null);
      
      // Reload integration data to show updated status
      loadIntegrationData();
    } catch (error) {
      console.error('Error revoking integration:', error);
      setToastMsg('Failed to revoke integration');
    }
  };

  // Copy integration URL to clipboard
  const copyIntegrationUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setToastMsg('The link is copied.');
    } catch (err) {
      console.error('Failed to copy URL:', err);
      setToastMsg('Failed to copy link to clipboard');
    }
  };

  // Handle copy button click
  const handleCopyClick = (url: string) => {
    copyIntegrationUrl(url);
  };

  const handleRecreateIntegration = async () => {
    if (!integration || !integrationId) return;
    
    try {
      const updatedIntegration = await integrationService.recreateExpiredIntegration(
        integration.organization_id,
        integrationId,
        accessToken!,
        setUserAndToken
      );
      
      if (updatedIntegration.link_to_authorize) {
        setToastMsg('Integration re-created successfully. New authorization link generated.');
        // Show the integration modal with the new link (like OrganizationDetails does)
        setIntegrationUrl(updatedIntegration.link_to_authorize);
        setShowIntegrationModal(true);
        // Reload integration data to show updated status
        loadIntegrationData();
      } else {
        throw new Error('Failed to generate new integration link');
      }
    } catch (error) {
      console.error('Error re-creating integration:', error);
      setToastMsg('Failed to re-create integration');
    }
  };

  const handleSyncIncremental = async () => {
    if (!tenants.length || !integration) return;
    
    try {
      const firstTenant = tenants[0];
      await xeroService.startIncrementalSync(
        integration.organization_id,
        firstTenant.xero_tenant_id,
        accessToken!,
        setUserAndToken
      );
      setToastMsg('Incremental sync started');
      // Reload sync data after starting sync
      setTimeout(() => {
        loadIntegrationData();
      }, 1000);
    } catch (error) {
      console.error('Error starting incremental sync:', error);
      setToastMsg('Failed to start incremental sync');
    }
  };

  const handleSyncFull = async () => {
    if (!tenants.length || !integration) return;
    
    try {
      const firstTenant = tenants[0];
      await xeroService.startFullSync(
        integration.organization_id,
        firstTenant.xero_tenant_id,
        accessToken!,
        setUserAndToken
      );
      setToastMsg('Full sync started');
      // Reload sync data after starting sync
      setTimeout(() => {
        loadIntegrationData();
      }, 1000);
    } catch (error) {
      console.error('Error starting full sync:', error);
      setToastMsg('Failed to start full sync');
    }
  };

  const formatDate = (dateString: string) => {
    return formatTimestampWithTimezone(dateString);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600';
      case 'failed':
        return 'text-red-600';
      case 'in_progress':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return '✅';
      case 'failed':
        return '❌';
      case 'in_progress':
        return '⏳';
      default:
        return '❓';
    }
  };

  // Helper function to get sync result data for specific object types
  const getSyncResultData = (syncSummary: any, objectType: string) => {
    // Handle both initial sync format (with final_results wrapper) and incremental sync format (direct)
    let result = null;
    
    if (syncSummary?.final_results?.[objectType]) {
      // Initial sync format: syncSummary.final_results[objectType]
      result = syncSummary.final_results[objectType];
    } else if (syncSummary?.[objectType]) {
      // Incremental sync format: syncSummary[objectType]
      result = syncSummary[objectType];
    }
    
    if (!result) {
      return { status: 'N/A', total: 0, created: 0, updated: 0, deleted: 0 };
    }
    
    return {
      status: result.status || 'N/A',
      total: result.total || 0,
      created: result.created || 0,
      updated: result.updated || 0,
      deleted: result.deleted || 0
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading integration details...</span>
      </div>
    );
  }

  if (error || !integration) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {error || 'Integration not found'}
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/organizations/${integration?.organization_id}`)}
            className="text-blue-600 hover:text-blue-800 flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>Back to Organization</span>
          </button>
        </div>

        {/* Action Buttons */}
        {organizationIntegration?.authorization_status === 'pending' ? (
          <div className="flex space-x-3">
            <button
              onClick={handleRevokeIntegration}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              Revoke Integration
            </button>
            {organizationIntegration?.link_to_authorize && (
              <button
                onClick={() => handleCopyClick(organizationIntegration.link_to_authorize)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                Copy Link
              </button>
            )}
          </div>
        ) : organizationIntegration?.authorization_status === 'authorized' ? (
          <div className="flex space-x-3">
            <button
              onClick={handleRevokeIntegration}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              Revoke Integration
            </button>
            <button
              onClick={handleSyncIncremental}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              Sync Incremental
            </button>
            <button
              onClick={handleSyncFull}
              className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              Sync Full
            </button>
          </div>
        ) : organizationIntegration?.authorization_status === 'revoked' ? (
          <div className="flex space-x-3">
            <button
              onClick={handleRecreateIntegration}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              Re-create Integration
            </button>
          </div>
        ) : null}
      </div>

      {/* Integration Information and Tenant Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Integration Details */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Integration Information</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-500">Status:</span>
              <span className={`text-sm font-medium ${
                organizationIntegration?.authorization_status === 'revoked' 
                  ? 'text-red-600' 
                  : organizationIntegration?.authorization_status === 'pending'
                  ? 'text-yellow-600'
                  : 'text-green-600'
              }`}>
                {organizationIntegration?.authorization_status === 'revoked' 
                  ? 'Revoked' 
                  : organizationIntegration?.authorization_status === 'pending'
                  ? 'Pending Authorization'
                  : 'Active'
                }
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-500">Created:</span>
              <span className="text-sm text-gray-900">
                {formatDate(integration.created_at)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-500">Last Updated:</span>
              <span className="text-sm text-gray-900">
                {formatDate(integration.updated_at)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-500">Expires:</span>
              <span className="text-sm text-gray-900">
                {formatDate(integration.expires_at)}
              </span>
            </div>

            {organizationIntegration?.authorization_status === 'revoked' && (
              <>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Revoked At:</span>
                  <span className="text-sm text-red-600 font-medium">
                    {organizationIntegration.revoked_at ? formatDate(organizationIntegration.revoked_at) : 'N/A'}
                  </span>
                </div>
              </>
            )}
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-500">Scope:</span>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-900 max-w-xs truncate">
                  {integration.scope && integration.scope !== 'N/A' ? integration.scope.split(' ').slice(0, 3).join(' ') + '...' : 'N/A'}
                </span>
                {integration.scope && integration.scope !== 'N/A' && (
                  <button
                    onClick={() => setShowScopeModal(true)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium underline"
                  >
                    View Details
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tenant Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Tenant Information</h3>
          {tenants.length > 0 ? (
            <div className="space-y-3">
              {tenants.map((tenant) => (
                <div key={tenant.id} className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Tenant Name:</span>
                    <span className="text-sm text-gray-900">{tenant.tenant_name || 'Unnamed Tenant'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Tenant ID:</span>
                    <span className="text-sm text-gray-900">{tenant.xero_tenant_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Type:</span>
                    <span className="text-sm text-gray-900">{tenant.tenant_type || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Connection ID:</span>
                    <span className="text-sm text-gray-900">{tenant.xero_connection_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Default:</span>
                    <span className="text-sm text-gray-900">
                      {tenant.is_default ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Yes
                        </span>
                      ) : (
                        'No'
                      )}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-500">No tenants found</div>
          )}
        </div>
      </div>

      {/* Sync History Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 max-w-full">
          <div className="flex mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Sync History</h3>
          </div>
          {syncHistory && syncHistory.sync_history.length > 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto w-full">
                <table className="w-full divide-y divide-gray-200" style={{ tableLayout: 'fixed' }}>
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '15%' }}>
                        Date
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '10%' }}>
                        Type
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '15%' }}>
                        Status
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '12%' }}>
                        Contacts
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '12%' }}>
                        Invoices
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '12%' }}>
                        Payments
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '12%' }}>
                        Credit Notes
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '12%' }}>
                        Accounts
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '10%' }}>
                        Items
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '15%' }}>
                        Tracking
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {syncHistory.sync_history.map((sync) => {
                      const contacts = getSyncResultData(sync.sync_summary, 'contacts');
                      const invoices = getSyncResultData(sync.sync_summary, 'invoices');
                      const payments = getSyncResultData(sync.sync_summary, 'payments');
                      const creditNotes = getSyncResultData(sync.sync_summary, 'credit_notes');
                      const accounts = getSyncResultData(sync.sync_summary, 'accounts');
                      const items = getSyncResultData(sync.sync_summary, 'items');
                      const trackingCategories = getSyncResultData(sync.sync_summary, 'tracking_categories');

                      return (
                        <tr key={sync.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-sm text-gray-900 text-left">
                            {formatDate(sync.started_at)}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 text-left capitalize">
                            {sync.sync_type}
                          </td>
                          <td className="px-3 py-2 text-sm text-left">
                            <div className="flex flex-col gap-1">
                              <span className={`flex items-center gap-1 ${getStatusColor(sync.status)}`}>
                                {getStatusIcon(sync.status)}
                                <span className="capitalize">{sync.status.replace('_', ' ')}</span>
                              </span>
                              {sync.duration_seconds && (
                                <span>
                                  {sync.duration_seconds < 60 
                                    ? `${sync.duration_seconds}s`
                                    : sync.duration_seconds < 3600
                                    ? `${Math.floor(sync.duration_seconds / 60)}m ${sync.duration_seconds % 60}s`
                                    : `${Math.floor(sync.duration_seconds / 3600)}h ${Math.floor((sync.duration_seconds % 3600) / 60)}m`
                                  }
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-600 text-left">
                            {contacts.status === 'success' ? (
                              <span 
                                title={`Status: ${contacts.status}, Created: ${contacts.created}, Updated: ${contacts.updated}, Total: ${contacts.total}`}
                                className="cursor-help"
                              >
                                {contacts.status}<br />{contacts.created} / {contacts.updated} / {contacts.total}
                              </span>
                            ) : contacts.status !== 'N/A' ? (
                              <span 
                                title={`Status: ${contacts.status}, Created: ${contacts.created}, Updated: ${contacts.updated}, Total: ${contacts.total}`}
                                className="cursor-help"
                              >
                                {contacts.status}<br />{contacts.created} / {contacts.updated} / {contacts.total}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-2 py-2 text-sm text-gray-600 text-left">
                            {invoices.status === 'success' ? (
                              <span 
                                title={`Status: ${invoices.status}, Created: ${invoices.created}, Updated: ${invoices.updated}, Total: ${invoices.total}`}
                                className="cursor-help"
                              >
                                {invoices.status}<br />{invoices.created} / {invoices.updated} / {invoices.total}
                              </span>
                            ) : invoices.status !== 'N/A' ? (
                              <span 
                                title={`Status: ${invoices.status}, Created: ${invoices.created}, Updated: ${invoices.updated}, Total: ${invoices.total}`}
                                className="cursor-help"
                              >
                                {invoices.status}<br />{invoices.created} / {invoices.updated} / {invoices.total}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-2 py-2 text-sm text-gray-600 text-left">
                            {payments.status === 'success' ? (
                              <span 
                                title={`Status: ${payments.status}, Created: ${payments.created}, Updated: ${payments.updated}, Total: ${payments.total}`}
                                className="cursor-help"
                              >
                                {payments.status}<br />{payments.created} / {payments.updated} / {payments.total}
                              </span>
                            ) : payments.status !== 'N/A' ? (
                              <span 
                                title={`Status: ${payments.status}, Created: ${payments.created}, Updated: ${payments.updated}, Total: ${payments.total}`}
                                className="cursor-help"
                              >
                                {payments.status}<br />{payments.created} / {payments.updated} / {payments.total}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-2 py-2 text-sm text-gray-600 text-left">
                            {creditNotes.status === 'success' ? (
                              <span 
                                title={`Status: ${creditNotes.status}, Created: ${creditNotes.created}, Updated: ${creditNotes.updated}, Total: ${creditNotes.total}`}
                                className="cursor-help"
                              >
                                {creditNotes.status}<br />{creditNotes.created} / {creditNotes.updated} / {creditNotes.total}
                              </span>
                            ) : creditNotes.status !== 'N/A' ? (
                              <span 
                                title={`Status: ${creditNotes.status}, Created: ${creditNotes.created}, Updated: ${creditNotes.updated}, Total: ${creditNotes.total}`}
                                className="cursor-help"
                              >
                                {creditNotes.status}<br />{creditNotes.created} / {creditNotes.updated} / {creditNotes.total}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-2 py-2 text-sm text-gray-600 text-left">
                            {accounts.status === 'success' ? (
                              <span 
                                title={`Status: ${accounts.status}, Created: ${accounts.created}, Updated: ${accounts.updated}, Total: ${accounts.total}`}
                                className="cursor-help"
                              >
                                {accounts.status}<br />{accounts.created} / {accounts.updated} / {accounts.total}
                              </span>
                            ) : accounts.status !== 'N/A' ? (
                              <span 
                                title={`Status: ${accounts.status}, Created: ${accounts.created}, Updated: ${accounts.updated}, Total: ${accounts.total}`}
                                className="cursor-help"
                              >
                                {accounts.status}<br />{accounts.created} / {accounts.updated} / {accounts.total}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-2 py-2 text-sm text-gray-600 text-left">
                            {items.status === 'success' ? (
                              <span 
                                title={`Status: ${items.status}, Created: ${items.created}, Updated: ${items.updated}, Total: ${items.total}`}
                                className="cursor-help"
                              >
                                {items.status}<br />{items.created} / {items.updated} / {items.total}
                              </span>
                            ) : items.status !== 'N/A' ? (
                              <span 
                                title={`Status: ${items.status}, Created: ${items.created}, Updated: ${items.updated}, Total: ${items.total}`}
                                className="cursor-help"
                              >
                                {items.status}<br />{items.created} / {items.updated} / {items.total}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-2 py-2 text-sm text-gray-600 text-left">
                            {trackingCategories.status === 'success' ? (
                              <span 
                                title={`Status: ${trackingCategories.status}, Created: ${trackingCategories.created}, Updated: ${trackingCategories.updated}, Total: ${trackingCategories.total}`}
                                className="cursor-help"
                              >
                                {trackingCategories.status}<br />{trackingCategories.created} / {trackingCategories.updated} / {trackingCategories.total}
                              </span>
                            ) : trackingCategories.status !== 'N/A' ? (
                              <span 
                                title={`Status: ${trackingCategories.status}, Created: ${trackingCategories.created}, Updated: ${trackingCategories.updated}, Total: ${trackingCategories.total}`}
                                className="cursor-help"
                              >
                                {trackingCategories.status}<br />{trackingCategories.created} / {trackingCategories.updated} / {trackingCategories.total}
                              </span>
                            ) : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No sync history available
            </div>
          )}
        </div>
      </div>

      {/* Scope Details Modal */}
      {showScopeModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900">Scope Details</h3>
                <button
                  onClick={() => setShowScopeModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-4">
                <p className="text-sm text-gray-600 text-left">
                  Full scope permissions for this integration:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-800 font-mono break-words text-left">
                    {integration?.scope && integration.scope !== 'N/A' ? integration.scope : 'No scope information available'}
                  </div>
                </div>
                <div className="text-xs text-gray-500 text-left">
                  <p>• These permissions define what data the integration can access</p>
                  <p>• The scope is set during the OAuth authorization process</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Integration URL Modal */}
      <IntegrationUrlModal
        isOpen={showIntegrationModal}
        onClose={() => setShowIntegrationModal(false)}
        integrationUrl={integrationUrl}
        onCopy={handleCopyClick}
        title="New Integration Authorization Link"
        description="A new authorization link has been generated for this integration. Please copy and share this link with the user to complete the authorization process."
      />

      {/* Revoke Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRevokeModal}
        onClose={() => setShowRevokeModal(false)}
        title="Confirm Integration Revocation"
        message="Revoking the integration will also be deleting all the Xero data. The action will not be reversible."
        showWarningIcon={true}
        additionalInfo={
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs text-red-700">
              <strong>This will permanently delete:</strong>
            </p>
            <ul className="text-xs text-red-600 mt-1 space-y-1">
              <li>• All synced contacts, invoices, and payments</li>
              <li>• Chart of accounts and tracking categories</li>
              <li>• Integration credentials and settings</li>
              <li>• <strong>Note:</strong> Sync history will be preserved for audit purposes</li>
            </ul>
          </div>
        }
        actions={[
          {
            label: "Cancel",
            onClick: () => setShowRevokeModal(false),
            variant: "secondary"
          },
          {
            label: "Revoke and Delete Xero Data",
            onClick: confirmRevokeIntegration,
            variant: "danger"
          }
        ]}
      />

      {/* Toast Notification */}
      <ToastNotification
        toastMsg={toastMsg}
        setToastMsg={setToastMsg}
      />
    </div>
  );
};

export default IntegrationDetails;
